import { inject, observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Container, Form, Row } from 'reactstrap';
import { defineMessages } from 'react-intl';

import PropTypes from 'prop-types';
import CheckoutPageModules from '../../components/checkout/common/CheckoutPageModules';
import CheckoutPageTitle from '../../components/checkout/common/CheckoutPageTitle';
import CheckoutSummaryModule from '../../components/checkout/summary/CheckoutSummaryModule';
import ScrollReset from '../../components/common/ScrollReset';
import SEODescription from '../../components/head/SEODescription';
import SEOTitle from '../../components/head/SEOTitle';
import Page from '../../components/layout/Page';
import RightSidebarLayout from '../../components/layout/RightSidebarLayout';
import ContentForState from '../../components/loader/ContentForState';
import CheckoutPageSkeleton from '../../components/skeleton/CheckoutPageSkeleton';
import useFetchBanners from '../../hooks/data/useFetchBanners';
import useFetchManufacturers from '../../hooks/data/useFetchManufacturers';
import useActiveProduct from '../../hooks/product/useActiveProduct';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import AnalyticsService from '../../services/AnalyticsService';
import CartStore from '../../store/CartStore';
import CheckoutStore from '../../store/CheckoutStore';
import ConfigStore from '../../store/ConfigStore';
import ManufacturerStore from '../../store/ManufacturerStore';
import UIStore from '../../store/UIStore';
import TrackingStore from '../../store/TrackingStore';
import AdPages from '../../types/AdPages';
import CommonPage from '../../types/CommonPage';
import TrackingEventType from '../../types/TrackingPageType';
import { parsePath } from '../../util/queryString';

const messages = defineMessages({
  checkoutPageDescription: {
    id: 'checkout.checkoutPageDescription',
    defaultMessage: 'Welcome to the {storeName} checkout page',
  },
});

const CheckoutPage = ({
  analyticsService,
  adStore,
  cartStore,
  checkoutStore,
  configStore,
  manufacturerStore,
  trackingStore,
  uiStore,
}) => {
  const params = useParams();
  const location = useLocation();
  const history = useHistory();
  const { getActiveProductPath } = useActiveProduct({
    productPage: false,
    checkoutPage: true,
  });

  useEffect(() => {
    checkoutStore.loadOrder().then(onLoadResolve);

    return () => {
      // Cart needs to be updated separately
      cartStore.loadCart();
    };
  }, []);

  //shouldHaveAds return adStore.checkoutPageAds.zones.length > 0;
  useFetchBanners({
    adStore,
    bannerPage: AdPages.CHECKOUT_PAGE,
    shouldHaveAds: false,
  });
  useFetchManufacturers({ manufacturerStore });

  const onLoadResolve = () => {
    // TODO: Needs improving. History.replace should happen
    //  when something actually changes.
    updatePath();
    analyticsService.viewCartCheckout();
    configStore.integrations.spotmore.enabled && sendTrackingData();
  };

  const updatePath = () => {
    const { order } = checkoutStore;
    const hash = order.scroll_to_alert?.toLowerCase() || '';
    // Reset url params set by recommended product modal if customer has reloaded page while it's open with selected product collection variant.
    const productPath = getActiveProductPath({
      activeProductId: null,
      queryParams: {},
    });

    const productLocation = parsePath(productPath);

    /**
     * Notify url change listeners when url changes
     * Actual page scroll is in dom/hooks/useScrollToElement
     */
    history.replace({ ...location, ...productLocation, hash });
  };

  const sendTrackingData = () => {
    if (window.crmtracker) {
      window.crmtracker.sendData();
    }

    trackingStore.setPageView({
      visitedUrl: window.location.href,
      refererUrl: document.referrer,
    });
    trackingStore.postPageView();
  };

  const renderPageContent = () => {
    const { order } = checkoutStore;
    const { cartIsEmpty, noTotals, shouldUseProviderCustomerHandling } = order;
    const modulesComponent = <CheckoutPageModules />;

    if (cartIsEmpty && noTotals) {
      return modulesComponent;
    }

    // In KCO non-desktop view, the summary is rendered within CheckoutPageModules
    const isDesktop = uiStore.isDesktop;
    const shouldRenderSidebarSummary =
      !shouldUseProviderCustomerHandling || isDesktop;

    return (
      <Form noValidate name="checkout">
        <div className="CheckoutPage__title-area">
          <CheckoutPageTitle />
        </div>
        <Row className="RightSidebarLayout">
          <RightSidebarLayout
            sidebar={
              shouldRenderSidebarSummary ? <CheckoutSummaryModule /> : null
            }
            sidebarStickyBottom={isDesktop}
          >
            {modulesComponent}
          </RightSidebarLayout>
        </Row>
      </Form>
    );
  };

    return (
    <Page
      name={CommonPage.CHECKOUT}
      onDefinedTrackingPage={TrackingEventType.CHECKOUT}
      className="CheckoutPage"
    >
      <ScrollReset key={params.id} />
      <SEOTitle title={{ ...globalTranslations.checkoutTitle }} />
      <SEODescription
        content={{
          ...messages.checkoutPageDescription,
          values: { storeName: configStore.store.name },
        }}
      />
      <Container className="CheckoutPage__content">
        <ContentForState
          state={checkoutStore.state}
          error={checkoutStore.lastError}
          forPlaceHolder={<CheckoutPageSkeleton />}
          forLoaded={renderPageContent}
        />
      </Container>
      </Page>
    );
};

CheckoutPage.propTypes = {
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  manufacturerStore: modelOf(ManufacturerStore).isRequired,
  trackingStore: modelOf(TrackingStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
};

export default inject(
  'analyticsService',
  'cartStore',
  'checkoutStore',
  'configStore',
  'manufacturerStore',
  'trackingStore',
  'uiStore'
)(observer(CheckoutPage));
